import { Button, Grid, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import type { ReceivingFacilityContact } from "../../../interfaces/ReceivingFacility";
import type { DepartmentEditorProps } from "./types";
import * as S from "./styles";

export const emptyContact = (): ReceivingFacilityContact => ({
    name: "", phone: "", email: "", role: "", department: "", priority: 1,
});

export const PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

export function DepartmentEditor(props: DepartmentEditorProps) {
    const { t } = useTranslation();
    const updateContacts = (index: number, contacts: ReceivingFacilityContact[]) =>
        props.onChange(props.departments.map((department, i) =>
            i === index ? { ...department, contacts } : department
        ));

    return (
        <S.Root>
            <Typography variant="h6">{t("rf_departments")}</Typography>
            <Typography variant="body2">{t("rf_contacts_helper")}</Typography>
            {props.departments.map((department, index) => (
                <S.Department key={department.id ?? index}>
                    <S.Header>
                        <TextField
                            fullWidth
                            required
                            label={t("rf_department_name")}
                            value={department.name}
                            InputProps={{ readOnly: props.readOnly }}
                            onChange={(event) => props.onChange(props.departments.map((item, i) =>
                                i === index ? { ...item, name: event.target.value } : item
                            ))}
                        />
                        {!props.readOnly && (
                            <IconButton
                                aria-label={t("rf_remove_department")}
                                disabled={props.departments.length === 1}
                                onClick={() => props.onChange(props.departments.filter((_, i) => i !== index))}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </S.Header>
                    {department.contacts.map((contact, contactIndex) => (
                        <S.Contact key={contact.id ?? contactIndex}>
                            <S.Header>
                                <S.Title variant="subtitle2">
                                    {t("rf_contact_name")} {contactIndex + 1}
                                </S.Title>
                                {!props.readOnly && (
                                    <IconButton
                                        aria-label={t("rf_remove_contact")}
                                        disabled={department.contacts.length === 1}
                                        onClick={() => updateContacts(index, department.contacts.filter((_, i) => i !== contactIndex))}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </S.Header>
                            <Grid container spacing={2}>
                                {(["name", "phone", "email", "role"] as const).map((field) => (
                                    <Grid item xs={12} sm={6} key={field}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            required={field !== "role"}
                                            label={t(`rf_contact_${field}`)}
                                            type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                                            value={contact[field]}
                                            InputProps={{ readOnly: props.readOnly }}
                                            error={field === "phone" && !!contact.phone && !PHONE_PATTERN.test(contact.phone)}
                                            helperText={field === "phone" && !props.readOnly ? t("rf_phone_format") : undefined}
                                            onChange={(event) => updateContacts(index, department.contacts.map((item, i) =>
                                                i === contactIndex ? { ...item, [field]: event.target.value } : item
                                            ))}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </S.Contact>
                    ))}
                    {!props.readOnly && (
                        <Button startIcon={<AddIcon />} onClick={() => updateContacts(index, [...department.contacts, emptyContact()])}>
                            {t("rf_add_contact")}
                        </Button>
                    )}
                </S.Department>
            ))}
            {!props.readOnly && (
                <Button startIcon={<AddIcon />} onClick={() => props.onChange([
                    ...props.departments, { name: "", contacts: [emptyContact()] },
                ])}>
                    {t("rf_add_department")}
                </Button>
            )}
        </S.Root>
    );
}
